import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { todayLocal } from "@/lib/quotationPdf";

const CHANNELS = ["Email", "WhatsApp", "Hand delivery", "Other"];

export function MarkSentDialog({ quotation, open, onOpenChange, onDone }) {
  const [sentDate, setSentDate] = useState(todayLocal());
  const [via, setVia] = useState("Email");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setSentDate(todayLocal()); setVia("Email"); setNote(""); }
  }, [open]);
  const minDate = quotation?.created_at ? new Date(quotation.created_at).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await api.post(`/quotations/${quotation.id}/mark-sent`, { sent_date: sentDate, sent_via: via, note });
      toast.success(`${r.data.display_number} marked as sent`);
      onDone?.(r.data);
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="mark-sent-dialog">
        <DialogHeader>
          <DialogTitle>Mark {quotation?.display_number} as sent</DialogTitle>
          <DialogDescription>Record how and when you sent this quotation to the client. It will be locked from editing.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sent date</Label>
            <Input type="date" required value={sentDate} min={minDate} max={todayLocal()} onChange={(e) => setSentDate(e.target.value)} className="mt-1" data-testid="mark-sent-date" />
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sent via</Label>
            <Select value={via} onValueChange={setVia}>
              <SelectTrigger className="mt-1" data-testid="mark-sent-via"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CHANNELS.map((c) => <SelectItem key={c} value={c} data-testid={`mark-sent-via-${c.replace(/\s+/g, "-").toLowerCase()}`}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Note (optional)</Label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. Sent to purchase head on WhatsApp" className="mt-1 w-full border border-slate-200 rounded-md p-2 text-sm" data-testid="mark-sent-note" />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !sentDate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="mark-sent-confirm">{saving ? "Saving…" : "Confirm sent"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
